/** Shared Njim design tokens — customer + internal portals */
export const CONTROL_HEIGHT = 44;
export const CONTROL_RADIUS = "10px";
export const ICON_TEXT_GAP = 1; // MUI spacing unit = 8px

/** Shared Njim design tokens — customer + internal portals */
export const NJIM = {
  ink: "#162D3A",
  inkSoft: "#E7EEF0",
  mint: "#5DB7A7",
  mintLight: "#8AD7C8",
  mintForeground: "#0F2630",
  mintWash: "rgba(93,183,167,0.16)",
  textPrimary: "#1A2233",
  textSecondary: "#71809B",
  textMuted: "#94A3B8",
  muted: "#F3F7FB",
  border: "#E6EAF1",
  white: "#FFFFFF",
  success: "#16A34A",
  warning: "#E5A23A",
  danger: "#DC2626",
  dangerHover: "#B91C1C",
  info: "#1D4356",
  successWash: "rgba(22,163,74,0.10)",
  warningWash: "rgba(229,162,58,0.14)",
  dangerWash: "rgba(220,38,38,0.10)",
  infoWash: "rgba(29,67,86,0.10)",
  walletHero: "linear-gradient(118deg, #16333F 0%, #1D4356 52%, #142A35 100%)",
  walletHeroHover: "linear-gradient(118deg, #1c3f4d 0%, #245a72 52%, #183340 100%)",
  elevation: "0 1px 2px rgba(23,34,55,0.04), 0 4px 16px rgba(23,34,55,0.05)",
  elevationHover: "0 4px 12px rgba(23,34,55,0.08), 0 10px 28px rgba(23,34,55,0.10)",
  elevationLg: "0 4px 8px rgba(23,34,55,0.05), 0 16px 40px rgba(23,34,55,0.09)",
  easeOut: "cubic-bezier(0.23, 1, 0.32, 1)",
} as const;

/** @deprecated Use NJIM.ink — kept for gradual migration of `${BRAND}` interpolations */
export const BRAND = NJIM.ink;

export const panelSx = {
  bgcolor: "rgba(255,255,255,0.82)",
  backdropFilter: "blur(20px) saturate(120%)",
  WebkitBackdropFilter: "blur(20px) saturate(120%)",
  borderRadius: "12px",
  boxShadow: NJIM.elevation,
  border: `1px solid ${NJIM.border}`,
} as const;

/** Page wrapper — relies on Layout leaf background, no blue gradient */
export const pageShellSx = {
  minHeight: "calc(100vh - 64px)",
  background: "transparent",
  p: { xs: 2, md: 3 },
} as const;

export const glassCardSx = {
  ...panelSx,
  p: { xs: 2.25, sm: 3 },
  mb: 3,
  overflow: "hidden",
} as const;

export const metricCardSx = {
  ...glassCardSx,
  mb: 0,
  p: 2,
} as const;

export const sectionTitleRowSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 0.75,
} as const;

export const iconBubbleSx = {
  width: 36,
  height: 36,
  borderRadius: "12px",
  display: "grid",
  placeItems: "center",
  background: NJIM.mintWash,
  border: "1px solid rgba(93,183,167,0.25)",
} as const;

export const softBlockSx = {
  bgcolor: NJIM.muted,
  border: `1px solid ${NJIM.border}`,
  borderRadius: "12px",
  p: 2,
} as const;

export const softFieldSx = {
  border: `1px solid ${NJIM.border}`,
  borderRadius: "10px",
  background: "rgba(255,255,255,0.9)",
} as const;

/** Row of search/filter/action controls — wrap cleanly, align to field baseline. */
export const toolbarRowSx = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-end",
  gap: 1.5,
  width: "100%",
} as const;

export const whmFilterLabelSx = {
  fontSize: 11,
  color: NJIM.textSecondary,
  fontWeight: 600,
  mb: 0.75,
  textTransform: "uppercase",
  letterSpacing: "0.3px",
} as const;

export const whmFilterHeaderSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 1.5,
} as const;

export const whmFilterSelectSx = {
  ...softFieldSx,
  borderRadius: "8px",
  fontSize: 13,
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  height: CONTROL_HEIGHT,
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
    height: CONTROL_HEIGHT,
    boxSizing: "border-box",
  },
} as const;

export const searchFieldSx = {
  display: "flex",
  alignItems: "center",
  gap: ICON_TEXT_GAP,
  minWidth: 0,
  width: "100%",
  px: 1.5,
  height: CONTROL_HEIGHT,
  borderRadius: CONTROL_RADIUS,
  border: `1px solid ${NJIM.border}`,
  background: "rgba(255,255,255,0.9)",
  "&:focus-within": {
    borderColor: NJIM.mint,
  },
} as const;

export const primaryBtnSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "999px",
  px: 2.25,
  height: CONTROL_HEIGHT,
  minHeight: CONTROL_HEIGHT,
  color: "#ffffff !important",
  background: `${NJIM.walletHero} !important`,
  boxShadow: "0 4px 14px rgba(22,51,63,0.35) !important",
  "&:hover": {
    background: `${NJIM.walletHeroHover} !important`,
    boxShadow: "0 6px 18px rgba(22,51,63,0.45) !important",
  },
  "&:disabled": {
    opacity: 0.55,
    boxShadow: "none !important",
  },
} as const;

export const dangerBtnSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "999px",
  px: 2.25,
  height: CONTROL_HEIGHT,
  minHeight: CONTROL_HEIGHT,
  color: `${NJIM.white} !important`,
  background: `${NJIM.danger} !important`,
  boxShadow: "none !important",
  "&:hover": {
    background: `${NJIM.dangerHover} !important`,
    boxShadow: "none !important",
  },
  "&:disabled": {
    opacity: 0.55,
    boxShadow: "none !important",
  },
} as const;

export const ghostBtnSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "999px",
  px: 2.25,
  height: CONTROL_HEIGHT,
  minHeight: CONTROL_HEIGHT,
  color: `${NJIM.ink} !important`,
  background: "transparent !important",
  boxShadow: "none !important",
  "&:hover": {
    background: `${NJIM.muted} !important`,
  },
  "&:disabled": {
    opacity: 0.55,
  },
} as const;

export const secondaryBtnSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "999px",
  px: 2.25,
  height: CONTROL_HEIGHT,
  minHeight: CONTROL_HEIGHT,
  borderColor: "rgba(22,51,63,0.18) !important",
  color: `${NJIM.ink} !important`,
  background: "linear-gradient(118deg, rgba(22,51,63,0.04) 0%, rgba(29,67,86,0.08) 52%, rgba(20,42,53,0.04) 100%) !important",
  "&:hover": {
    borderColor: "rgba(29,67,86,0.35) !important",
    background: "linear-gradient(118deg, rgba(22,51,63,0.1) 0%, rgba(29,67,86,0.16) 52%, rgba(20,42,53,0.1) 100%) !important",
  },
} as const;

export const containerSx = {
  maxWidth: 1180,
  mx: "auto",
  px: { xs: 1, sm: 2, md: 0 },
} as const;

export const hoverLiftSx = {
  transition: `box-shadow 150ms ${NJIM.easeOut}, transform 150ms ${NJIM.easeOut}`,
  "@media (hover: hover) and (pointer: fine)": {
    "&:hover": {
      boxShadow: NJIM.elevationHover,
      transform: "translateY(-2px)",
    },
  },
  "&:active": {
    transform: "scale(0.99)",
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "box-shadow 150ms ease",
    "&:hover": { transform: "none" },
    "&:active": { transform: "none" },
  },
} as const;

export const focusRingSx = {
  "&:focus-visible": {
    outline: `2px solid ${NJIM.mint}`,
    outlineOffset: 2,
  },
} as const;

export const tabularSx = {
  fontVariantNumeric: "tabular-nums",
} as const;

export const codKpiGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    sm: "repeat(3, minmax(0, 1fr))",
    md: "repeat(5, minmax(0, 1fr))",
  },
  gap: 2,
  mb: 3,
} as const;

/** KPI tile — border-box so padding never spills into the next grid row. */
export const codKpiCardSx = {
  boxSizing: "border-box",
  minWidth: 0,
  p: 2,
  borderRadius: "14px",
  border: `1px solid ${NJIM.border}`,
  background: NJIM.white,
  boxShadow: NJIM.elevation,
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  position: "relative",
  overflow: "hidden",
  ...hoverLiftSx,
} as const;

export const codTableSx = {
  width: "100%",
  border: `1px solid ${NJIM.border}`,
  borderRadius: "12px",
  overflow: "hidden",
  "& .MuiTableCell-root": {
    padding: "12px 14px",
    borderBottom: `1px solid ${NJIM.border}`,
    fontSize: "13px",
    color: NJIM.textPrimary,
  },
  "& .MuiTableBody .MuiTableRow-root:hover": {
    backgroundColor: NJIM.muted,
  },
  "& .MuiTableCell-head": {
    position: "sticky",
    top: 0,
    zIndex: 1,
    bgcolor: NJIM.muted,
    fontWeight: 600,
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    color: NJIM.textSecondary,
  },
  "& .MuiTableCell-alignRight": {
    fontVariantNumeric: "tabular-nums",
  },
} as const;

export const codTableHeadCellSx = {
  fontWeight: 600,
  color: NJIM.textSecondary,
  fontSize: "12px !important",
  textTransform: "uppercase" as const,
  letterSpacing: "0.3px",
  background: NJIM.muted,
} as const;

export const successBtnSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "999px",
  px: 2.25,
  height: CONTROL_HEIGHT,
  minHeight: CONTROL_HEIGHT,
  minWidth: 40,
  color: `${NJIM.white} !important`,
  background: `${NJIM.success} !important`,
  boxShadow: "none !important",
  "&:hover": {
    background: `${NJIM.success} !important`,
    filter: "brightness(0.95)",
  },
  "&:active": {
    transform: "scale(0.97)",
  },
  "&:disabled": {
    opacity: 0.55,
    boxShadow: "none !important",
  },
} as const;

export const dangerOutlinedBtnSx = {
  textTransform: "none",
  fontWeight: 600,
  borderRadius: "999px",
  px: 2.25,
  height: CONTROL_HEIGHT,
  minHeight: CONTROL_HEIGHT,
  minWidth: 40,
  color: `${NJIM.danger} !important`,
  border: `1px solid ${NJIM.danger} !important`,
  background: `${NJIM.white} !important`,
  boxShadow: "none !important",
  "&:hover": {
    background: `${NJIM.dangerWash} !important`,
  },
  "&:active": {
    transform: "scale(0.97)",
  },
  "&:disabled": {
    opacity: 0.55,
  },
} as const;

export const quantityStepperSx = {
  display: "inline-flex",
  alignItems: "center",
  height: 40,
  borderRadius: CONTROL_RADIUS,
  border: `1px solid ${NJIM.border}`,
  background: NJIM.white,
  overflow: "hidden",
  "& input": {
    width: 48,
    height: "100%",
    border: "none",
    outline: "none",
    textAlign: "center",
    fontWeight: 600,
    fontSize: 14,
    fontVariantNumeric: "tabular-nums",
    color: NJIM.ink,
    background: "transparent",
  },
} as const;

export const stickySummarySx = {
  position: "sticky",
  bottom: 12,
  zIndex: 3,
  mt: 2,
  p: 2,
  borderRadius: "14px",
  border: `1px solid ${NJIM.border}`,
  background: NJIM.white,
  boxShadow: NJIM.elevationLg,
} as const;

export const iconBtnSx = {
  width: 40,
  height: 40,
  color: NJIM.ink,
  border: `1px solid ${NJIM.border}`,
  background: NJIM.white,
  "&:hover": {
    background: NJIM.mintWash,
    borderColor: NJIM.mint,
  },
} as const;

export const filterSelectSx = {
  minWidth: 180,
  height: CONTROL_HEIGHT,
  background: NJIM.white,
  borderRadius: CONTROL_RADIUS,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: NJIM.border,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: NJIM.mint,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: NJIM.mint,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: CONTROL_HEIGHT,
    boxSizing: "border-box",
  },
} as const;

export const codTabBarContainerSx = {
  background: NJIM.muted,
  borderRadius: "999px",
  border: `1px solid ${NJIM.border}`,
  p: 0.5,
  overflow: "hidden",
} as const;

export const codTabBarSx = {
  width: "100%",
  minHeight: 44,
  padding: "2px",
  ".MuiTabs-flexContainer": {
    gap: "4px",
  },
  "& .MuiTabs-scroller": {
    overflowX: "auto !important",
  },
  "& .MuiTab-root": {
    flex: { xs: "0 0 auto", md: 1 },
    minWidth: { xs: "max-content", md: 0 },
    padding: "8px 14px",
    fontWeight: 600,
    textTransform: "none",
    fontSize: 13,
    borderRadius: "999px",
    minHeight: 40,
    transition: `background-color 180ms ${NJIM.easeOut}, color 180ms ${NJIM.easeOut}, box-shadow 180ms ${NJIM.easeOut}`,
    color: NJIM.textSecondary,
    "&:focus-visible": {
      outline: `2px solid ${NJIM.mint}`,
      outlineOffset: 2,
    },
    "&.Mui-selected": {
      background: NJIM.walletHero,
      color: NJIM.white,
      boxShadow: "0 2px 8px rgba(22,51,63,0.25)",
    },
    "&:not(.Mui-selected):hover": {
      backgroundColor: NJIM.mintWash,
      color: NJIM.ink,
    },
  },
  ".MuiTabs-indicator": {
    display: "none",
  },
  "@media (prefers-reduced-motion: reduce)": {
    "& .MuiTab-root": {
      transition: "none",
    },
  },
} as const;

export const codContentCardSx = {
  mt: 1.5,
  ...panelSx,
  p: { xs: 2, md: 3 },
} as const;
